import { Bell, Calendar, Check, ChevronDown, ChevronUp, Clock, Plus, Users } from 'react-feather';
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import groupBy from 'lodash/groupBy';
import isEqual from 'lodash/isEqual';
import map from 'lodash/map';
import pick from 'lodash/pick';
import styled, { css } from 'styled-components';
import times from 'lodash/times';
import uniq from 'lodash/uniq';
import upperFirst from 'lodash/upperFirst';

import { AssignmentType, BusinessRegistrationInput, CreateRegistrationInput } from '__generated__/graphql';

import { Availability, Quantities } from '../useQuantities';
import {
  CheckoutStep, Event, Product, Promotion, Ticket, TimeSlot, getAdditionalProducts, getTicketsForSale,
} from '../helpers';
import { mergeUrlParams, replaceUrlParams, resizedImageUrl, toggleInList } from '../../../common/helpers';
import { useEvent } from '../../Common/EventProvider';
import CheckoutContext from '../CheckoutContext';
import DiscountInfo from '../DiscountInfo';
import PriceRange from '../../../common/Common/PriceRange';
import SC from '../../../common/UI/SC';
import SoldOutLabel from '../SoldOutLabel';
import SportIcon from '../../../common/Common/SportIcon';
import StartDate from '../../../common/Common/StartDate';
import UI from '../../../common/UI';
import useCheckoutUrlState from '../useCheckoutUrlState';
import useLocale from '../../../common/useLocale';
import useTimeSlotFormatter from '../../../common/Tickets/useTimeSlotFormatter';

const TicketSelector = () => {
  const { t } = useTranslation();
  const { event, ticketCategories } = useEvent();
  const [{ showCategories }] = useCheckoutUrlState();

  const {
    setRegistrations,
    setBusinessRegistrations,
    quantities,
    availability,
    validators: { [CheckoutStep.Tickets]: { errors } },
    personalisation,
  } = useContext(CheckoutContext);

  const selectedTicketCategoryIds = useMemo(
    () => ticketCategories
      // Initially expand a ticket category if is the only one or if one its tickets has been selected.
      .filter((ticketCategory) => ticketCategories.length === 1 || quantities.ticketCategories[ticketCategory.id] > 0)
      .map((ticketCategory) => ticketCategory.id),
    [ticketCategories, quantities],
  );

  const getInitialTicketCategoryIds = useCallback(() => uniq([
    ...showCategories,
    ...selectedTicketCategoryIds,
  ]), [showCategories, selectedTicketCategoryIds]);

  const [activeTicketCategoryIds, setActiveTicketCategoryIds] = useState(getInitialTicketCategoryIds);

  const ticketCategoriesRef = useRef(ticketCategories.map((ticketCategory) => ticketCategory.id).join());

  /**
   * Reset the active ticket categories when the list of ticket categories changes.
   * This can happen when an invitation code is entered.
   */
  useEffect(() => {
    const ticketCategoryIds = ticketCategories.map((ticketCategory) => ticketCategory.id).join();

    if (ticketCategoryIds !== ticketCategoriesRef.current) {
      setActiveTicketCategoryIds(getInitialTicketCategoryIds);
      ticketCategoriesRef.current = ticketCategoryIds;
    }
  }, [ticketCategories, getInitialTicketCategoryIds]);

  const handleBusinessTicketChange = useCallback(
    (quantity: number, ticket: Ticket, promotion: Promotion, timeSlot?: TimeSlot) => {
      setBusinessRegistrations((registrations) => {
        // Find the index of the promotion and time slot combination (if it already exists)
        const index = registrations.findIndex(
          (registration) => registration.promotion.id === promotion.id && registration.time_slot?.id === timeSlot?.id,
        );
        const registration = registrations[index];
        const newRegistrations = [...registrations];

        // Add automatic ticket fees and required products as upgrades
        const upgrades = getAdditionalProducts(promotion, event.products_for_sale, true);

        const newRegistration: BusinessRegistrationInput = {
          ...registration,
          promotion: { id: promotion.id },
          time_slot: timeSlot ? { id: timeSlot.id } : null,
          upgrades: registration?.upgrades || upgrades,
          quantity,
        };

        if (index > -1) {
          if (newRegistration.quantity === 0) {
            newRegistrations.splice(index, 1);
          } else {
            newRegistrations[index] = newRegistration;
          }
        } else {
          newRegistrations.push(newRegistration);
        }

        return newRegistrations;
      }, event);
    },
    [setBusinessRegistrations, event],
  );

  const handleChange = useCallback(
    (quantity: number, ticket: Ticket, promotion: Promotion, timeSlot?: TimeSlot) => {
      if (ticket.business) {
        handleBusinessTicketChange(quantity, ticket, promotion, timeSlot);
      } else {
        setRegistrations(
          (registrations) => insertRegistration(
            registrations,
            ticket,
            promotion,
            timeSlot,
            quantity * ticket.units,
            event.products_for_sale,
            ticket.assignment_type !== AssignmentType.afterward || personalisation.requirePersonalisationGlobally,
          ),
          event,
        );
      }
    },
    [event, handleBusinessTicketChange, setRegistrations, personalisation.requirePersonalisationGlobally],
  );

  /**
   * Only returns the quantities that are needed by one TicketSelect. This helps to make sure we don't
   * needlessly rerender all TicketSelects when a ticket is added, which can get quite expensive.
   */
  const getTicketQuantitiesAndAvailability = (ticket: Ticket) => {
    const promotionIds: string[] = [];

    if (ticket.current_discount) {
      // Include promotion IDs of all tickets with the same discount, because the DiscountInfo
      // component requires them (some discounts are applicable to combinations of tickets)
      const discount = ticket.current_discount;

      getTicketsForSale(event).forEach((ticket) => {
        if (ticket.current_discount?.id === discount.id) {
          ticket.promotions_for_sale.forEach((promotion) => {
            promotionIds.push(promotion.id);
          });
        }
      });

      event.products_for_sale.forEach((product) => {
        if (product.current_discount?.id === discount.id) {
          product.promotions_for_sale.forEach((promotion) => {
            promotionIds.push(promotion.id);
          });
        }
      });
    } else {
      // If there is no discount, the TicketSelect only needs the quantities for this specific ticket
      ticket.promotions_for_sale.forEach((promotion) => {
        promotionIds.push(promotion.id);
      });
    }

    return {
      quantities: {
        ticketCategories: {}, // Individual TicketSelects don't need this.
        tickets: {
          [ticket.id]: quantities.tickets[ticket.id],
        },
        promotions: pick(quantities.promotions, promotionIds),
        timeSlots: pick(quantities.timeSlots, promotionIds),
        products: {},
        productVariants: {},
        purchases: pick(quantities.purchases, promotionIds),
        hasPreregistration: ticket.preregistration,
        hasBusinessTicket: ticket.business,
        hasConsumerTicket: !ticket.allow_companies,
        allTickets: promotionIds.length,
      },
      availability: {
        promotions: pick(availability.promotions, promotionIds),
        timeSlots: pick(availability.timeSlots, promotionIds),
        productVariants: {},
      },
    };
  };

  return (
    <UI.FormGrid sc={{ gutter: 0.5 }}>
      {ticketCategories.map((ticketCategory) => (
        <UI.Div key={ticketCategory.id}>
          {ticketCategories.length > 1 && (
            <TicketCategoryButton
              to={mergeUrlParams({
                showCategories: toggleInList(activeTicketCategoryIds, ticketCategory.id),
              })}
              onClick={() => setActiveTicketCategoryIds(toggleInList(activeTicketCategoryIds, ticketCategory.id))}
              sc={{
                active: activeTicketCategoryIds.includes(ticketCategory.id),
                selected: quantities.ticketCategories[ticketCategory.id] > 0,
              }}
              replace
            >
              <UI.Icon sc={{ size: 2 }}>
                <SportIcon type={ticketCategory.sport_type} />
              </UI.Icon>
              <UI.Div style={{ width: '100%' }}>
                <UI.H4>
                  {ticketCategory.title}
                </UI.H4>
                <UI.Div sc={{ small: true, color: 'gray.600' }}>
                  <UI.Delimit>
                    {ticketCategory.start_date && (
                      <StartDate date={ticketCategory.start_date} />
                    )}
                    {...ticketCategory.formatted_distances}
                  </UI.Delimit>
                </UI.Div>
              </UI.Div>

              <UI.Badge sc={{ size: 24 }}>
                <UI.Icon sc={{ size: 1.5 }}>
                  {quantities.ticketCategories[ticketCategory.id] > 0
                    ? <Check strokeWidth={1.5} />
                    : (activeTicketCategoryIds.includes(ticketCategory.id)
                      ? <ChevronUp strokeWidth={1.5} />
                      : <ChevronDown strokeWidth={1.5} />)}
                </UI.Icon>
              </UI.Badge>
            </TicketCategoryButton>
          )}
          <UI.AnimateHeight isVisible={activeTicketCategoryIds.includes(ticketCategory.id)}>
            <UI.InputGroup sc={{ invalid: !!errors?.tickets }}>
              <UI.FormGrid sc={{ gutter: 0.5, pt: ticketCategories.length > 1 ? 2 : 0 }}>
                {ticketCategory.tickets_for_sale.map((ticket) => (
                  <TicketSelect
                    event={event}
                    ticket={ticket}
                    {...getTicketQuantitiesAndAvailability(ticket)}
                    onChange={handleChange}
                    key={ticket.id}
                  />
                ))}
              </UI.FormGrid>
              <UI.ErrorMessages attribute={t('selector.tickets')} errors={errors.tickets} sc={{ mt: 2 }} />
            </UI.InputGroup>
          </UI.AnimateHeight>
        </UI.Div>
      ))}
    </UI.FormGrid>
  );
};

interface TicketSelectProps {
  event: Event;
  ticket: Ticket;
  quantities: Quantities;
  availability: Availability;
  onChange: (quantity: number, ticket: Ticket, promotion: Promotion, timeSlot?: TimeSlot) => void;
}

/**
 * Memoized using Lodash' isEqual() method because the quantities and availabilities are not stable.
 * This prevents unnecessary rerenders which is especially useful when there are many tickets.
 */
const TicketSelect = memo(
  ({ event, ticket, quantities, availability, onChange }: TicketSelectProps) => {
    const { t } = useTranslation();
    const { formatNumber } = useLocale();

    const quantity = quantities.tickets[ticket.id];
    const selected = quantity > 0;
    const defaultPromotion = ticket.promotions_for_sale[0];
    const ticketHasAmount = Math.max(...ticket.promotions_for_sale.map(({ amount }) => amount)) > 0;

    const [showingQuantitySelect, setShowingQuantitySelect] = useState(selected);

    /**
     * If the form state gets changed from somewhere else, update the UI accordingly.
     */
    useEffect(() => {
      if (selected) {
        setShowingQuantitySelect(true);
      }
    }, [selected]);

    /** Only show prices next to promotions if they have different prices */
    const showPromotionPrices = useMemo(
      () => uniq(map(ticket.promotions_for_sale, 'amount')).length > 1,
      [ticket],
    );

    const options = Math.max(1, ticket.upcoming_time_slots.length) * ticket.promotions_for_sale.length;

    /**
     * Selects exactly 1 of the ticket.
     */
    const select = () => {
      if (!ticket.is_sold_out) {
        setShowingQuantitySelect(true);

        if (options === 1) {
          onChange(1, ticket, defaultPromotion, ticket.upcoming_time_slots[0]);
        }
      }
    };

    const canBeSelected = useMemo(() => {
      if (ticket.current_max_per_order === 0 || ticket.is_sold_out) {
        return false;
      }

      for (let i = 0; i < ticket.promotions_for_sale.length; i++) {
        const promotion = ticket.promotions_for_sale[i];

        for (let j = 0; j < ticket.upcoming_time_slots.length; j++) {
          const timeSlot = ticket.upcoming_time_slots[j];

          if (availability.timeSlots[promotion.id][timeSlot.id] > 0) {
            return true;
          }
        }

        if (ticket.upcoming_time_slots.length === 0) {
          if (availability.promotions[promotion.id] > 0) {
            return true;
          }
        }
      }

      return false;
    }, [ticket, availability]);

    const handleDeselect = useCallback(() => setShowingQuantitySelect(false), [setShowingQuantitySelect]);

    const showResale = ticket.is_sold_out && event.enable_public_resale && ticket.allow_resale
      && ticket.cached_registrations_for_sale_count > 0;

    const showWaitlist = ticket.is_sold_out && event.enable_waitlist && !showResale;

    const showInterestList = ticket.is_sold_out && event.enable_interest_list && event.interest_list
      && !showResale && !showWaitlist;

    // If the ticket cannot be selected because the ticket category's maximum is reached, then display 'Full'.
    // For group tickets, apply rounding so that the number of spots left is always divisible by the group size.
    const spotsLeft = ticket.published_availability !== null
      ? (canBeSelected || quantity > 0
        ? ticket.published_availability - (ticket.published_availability % ticket.units)
        : 0)
      : null;

    return (
      <UI.Card
        sc={{ outline: selected ? 'secondary' : 'gray.200', borderWidth: selected ? 2 : 1, mb: 0 }}
      >
        <UI.GridContainer sc={{ gutter: 0.75 }}>
          {ticket.image && (
            <UI.Div>
              <UI.Image sc={{ ratio: 2 }}>
                <UI.Div>
                  <img
                    src={resizedImageUrl(ticket.image.url, 800)}
                    alt={ticket.title}
                  />
                </UI.Div>
              </UI.Image>
            </UI.Div>
          )}
          <UI.GridContainer sc={{ gutter: 0.25, color: ticket.is_sold_out ? 'gray.400' : undefined }}>
            <UI.H4 sc={{ strong: true, color: ticket.is_sold_out ? 'gray.400' : undefined }}>
              {ticket.title}
              {' '}
              {ticket.promotions_for_sale.length === 1 && !defaultPromotion.standard && (
                <>
                  <UI.Span sc={{ muted: true }}> • </UI.Span>
                  {defaultPromotion.title}
                </>
              )}
            </UI.H4>
            <UI.FlexContainer sc={{ alignItems: 'center', spaceX: 2 }}>
              <UI.Div style={{ fontWeight: 500 }}>
                <PriceRange prices={ticket.promotions_for_sale} />
              </UI.Div>
              {ticket.units > 1 && (
                <UI.Label sc={{ small: true }}>
                  <UI.Icon>
                    <Users />
                  </UI.Icon>
                  {' '}
                  {formatNumber(ticket.units)}
                </UI.Label>
              )}
              {spotsLeft !== null && (
                <UI.Label
                  sc={{
                    background: spotsLeft > 100
                      ? 'primary.25'
                      : (spotsLeft === 0 ? 'error.50' : 'info.100'),
                    color: spotsLeft > 100
                      ? 'primary.800'
                      : (spotsLeft === 0 ? 'error.700' : 'info.800'),
                    small: true,
                  }}
                >
                  {spotsLeft === 0 ? t('selector.full') : t('selector.n_spots_left', {
                    count: spotsLeft,
                    value: formatNumber(spotsLeft),
                  })}
                </UI.Label>
              )}
              {ticket.charity?.logo_url && (
                <UI.Div style={{ height: 18, maxWidth: 60 }}>
                  <img
                    src={ticket.charity.logo_url}
                    alt={ticket.charity.title}
                    style={{ objectFit: 'contain', width: '100%', height: '100%' }}
                  />
                </UI.Div>
              )}
              {ticket.business && ticket.vat_percentage > 0 && ticketHasAmount && (
                <UI.Small sc={{ muted: true }}>
                  {t('selector.includes_vat_percentage', { percentage: formatNumber(ticket.vat_percentage) })}
                </UI.Small>
              )}
            </UI.FlexContainer>
          </UI.GridContainer>
          {ticket.description && (
            <UI.Div sc={{ color: 'gray.600' }}>
              <UI.HTML html={ticket.description} />
            </UI.Div>
          )}
          {!ticket.is_sold_out ? (
            <UI.Div style={{ position: 'relative', minHeight: 40 }}>
              {!showingQuantitySelect && (
                <UI.GridContainer
                  sc={{ columns: options > 1 ? '5fr 2fr' : '1fr', alignVertical: 'center' }}
                  style={{ position: 'absolute', width: '100%', zIndex: 1 }}
                >
                  <UI.Div>
                    <UI.Button
                      onClick={() => select()}
                      sc={{ brand: 'secondary' }}
                      disabled={!canBeSelected}
                      aria-label={options === 1
                        ? t('selector.add_item', { title: ticket.title })
                        : t('selector.choose_item', { title: ticket.title })}
                    >
                      {options === 1 && (
                        <>
                          <UI.Icon>
                            <Plus />
                          </UI.Icon>
                          {' '}
                          {t('selector.add')}
                        </>
                      )}
                      {options > 1 && (
                        <>
                          {t('selector.choose')}
                          {' '}
                          <UI.Icon>
                            <ChevronDown />
                          </UI.Icon>
                        </>
                      )}
                    </UI.Button>
                  </UI.Div>
                  {options > 1 && (
                    <UI.Div sc={{ textAlign: 'right', muted: true, noWrap: true }}>
                      {t('selector.n_options', { count: options })}
                    </UI.Div>
                  )}
                </UI.GridContainer>
              )}

              <UI.AnimateHeight
                duration={options === 1 ? 0 : undefined}
                isVisible={showingQuantitySelect}
              >
                <UI.GridContainer sc={{ gutter: 0.75 }}>
                  {ticket.promotions_for_sale.map((promotion) => (
                    <PromotionSelect
                      ticket={ticket}
                      promotion={promotion}
                      promotionQuantity={quantities.promotions[promotion.id]}
                      promotionAvailability={availability.promotions[promotion.id]}
                      timeSlotQuantities={quantities.timeSlots[promotion.id]}
                      timeSlotAvailabilities={availability.timeSlots[promotion.id]}
                      onChange={onChange}
                      onDeselect={handleDeselect}
                      showPrice={showPromotionPrices}
                      key={promotion.id}
                    />
                  ))}
                  {ticket.current_discount && (
                    <DiscountInfo event={event} discount={ticket.current_discount} quantities={quantities} />
                  )}
                  {options > 1 && (
                    <UI.Div>
                      <UI.HR sc={{ mt: 0, mb: [2.25, 3] }} />
                      <UI.A
                        onClick={() => setShowingQuantitySelect(false)}
                        sc={{ disabled: selected }}
                        aria-disabled={selected}
                        role="button"
                      >
                        <UI.Icon>
                          <ChevronUp />
                        </UI.Icon>
                        {' '}
                        {t('show_less')}
                      </UI.A>
                    </UI.Div>
                  )}
                </UI.GridContainer>
              </UI.AnimateHeight>
            </UI.Div>
          ) : (
            <UI.FlexContainer sc={{ alignItems: 'start', wrap: 'wrap', gap: 0.25 }}>
              <SoldOutLabel title={ticket.title} />
              {showResale && (
                <UI.LinkButton
                  sc={{ brand: 'secondary' }}
                  to={`/${event.id}/resale${replaceUrlParams({ ticket: ticket.id })}`}
                  aria-label={t('selector.ticket_resale_item', { item: ticket.title })}
                >
                  <UI.Icon>
                    <UI.Icons.Resale />
                  </UI.Icon>
                  {' '}
                  {t('ticket_resale')}
                  {' ('}
                  {formatNumber(ticket.cached_registrations_for_sale_count)}
                  )
                </UI.LinkButton>
              )}
              {showWaitlist && (
                <UI.LinkButton
                  sc={{ brand: 'secondary' }}
                  to={`/${event.id}/waitlist/${ticket.id}`}
                  aria-label={t('selector.waitlist_item', { item: ticket.title })}
                >
                  <UI.Icon>
                    <UI.Icons.Hourglass />
                  </UI.Icon>
                  {' '}
                  {t('waitlist.waitlist')}
                </UI.LinkButton>
              )}
              {showInterestList && (
                <UI.LinkButton sc={{ brand: 'secondary' }} to={`/${event.id}/interest`}>
                  <UI.Icon>
                    <Bell />
                  </UI.Icon>
                  {' '}
                  {t('interest_list.receive_updates')}
                </UI.LinkButton>
              )}
            </UI.FlexContainer>
          )}
        </UI.GridContainer>
      </UI.Card>
    );
  },
  (prevProps, newProps) => isEqual(prevProps, newProps),
);

TicketSelect.displayName = 'TicketSelect';

interface PromotionSelectProps {
  ticket: Ticket;
  promotion: Promotion;
  promotionQuantity: number;
  promotionAvailability: number;
  timeSlotQuantities: { [timeSlotId: string]: number; };
  timeSlotAvailabilities: { [timeSlotId: string]: number; };
  onChange: (quantity: number, ticket: Ticket, promotion: Promotion, timeSlot?: TimeSlot) => void;
  onDeselect: () => void;
  showPrice?: boolean;
}

/**
 * Memoized using Lodash' isEqual() method because timeSlotQuantities and timeSlotAvailabilities are not stable.
 * This prevents unnecessary rerenders which is especially useful when there are many promotions or time slots.
 */
const PromotionSelect = memo(
  ({
    ticket, promotion, promotionQuantity, promotionAvailability, timeSlotQuantities, timeSlotAvailabilities,
    onChange, onDeselect, showPrice = true,
  }: PromotionSelectProps) => {
    const { t } = useTranslation();
    const { formatCurrency } = useLocale();

    const [focused, setFocused] = useState(false);

    const handleChange = useCallback(
      (quantity: number) => {
        onChange(quantity, ticket, promotion);

        if (ticket.promotions_for_sale.length === 1 && ticket.upcoming_time_slots.length <= 1
          && quantity === 0 && !focused) {
          onDeselect();
        }
      },
      [onChange, ticket, promotion, focused, onDeselect],
    );

    const groupedTimeSlots = groupBy(ticket.upcoming_time_slots, 'start_date');

    const isMultiDate = Object.keys(groupedTimeSlots).length > 1;

    const getTimeSlotQuantitiesAndAvailability = (timeSlots: TimeSlot[]) => {
      const timeSlotIds = timeSlots.map(({ id }) => id);

      return {
        timeSlotQuantities: pick(timeSlotQuantities, timeSlotIds),
        timeSlotAvailabilities: pick(timeSlotAvailabilities, timeSlotIds),
      };
    };

    const handleFocus = useCallback(() => {
      setFocused(true);
    }, [setFocused]);

    const handleBlur = useCallback(() => {
      setFocused(false);
    }, [setFocused]);

    return (
      <UI.Div>
        {ticket.promotions_for_sale.length > 1 && (
          <UI.HR sc={{ mt: 0, mb: [2.25, 3] }} />
        )}

        <UI.GridContainer sc={{ gutter: 0.75 }}>
          {(ticket.upcoming_time_slots.length === 0 || ticket.promotions_for_sale.length > 1) && (
            <UI.GridContainer
              sc={{
                columns: ticket.promotions_for_sale.length > 1 && ticket.upcoming_time_slots.length === 0
                  ? '1fr 143px' : '1fr',
                alignVertical: 'center',
              }}
            >
              {ticket.promotions_for_sale.length > 1 && (
                <UI.GridContainer
                  sc={{
                    columns: ticket.upcoming_time_slots.length > 0 ? '1fr 1fr' : '1fr',
                    gutter: 0,
                  }}
                >
                  <UI.Div>
                    {ticket.upcoming_time_slots.length === 0 && (
                      <UI.InputLabel>{promotion.title || t('selector.standard')}</UI.InputLabel>
                    )}
                    {ticket.upcoming_time_slots.length > 0 && (
                      <UI.Legend>{promotion.title || t('selector.standard')}</UI.Legend>
                    )}
                  </UI.Div>
                  {showPrice && (
                    <UI.Div sc={{ textAlign: ticket.upcoming_time_slots.length > 0 ? 'right' : undefined }}>
                      {promotion.amount === 0 && t('selector.free')}
                      {promotion.amount > 0 && formatCurrency(promotion.amount)}
                    </UI.Div>
                  )}
                </UI.GridContainer>
              )}
              {promotion.is_sold_out && (
                <UI.Div sc={{ textAlign: 'right' }}>
                  <SoldOutLabel title={`${ticket.title} / ${promotion.title || t('selector.standard')}`} />
                </UI.Div>
              )}
              {!promotion.is_sold_out && ticket.upcoming_time_slots.length === 0 && (
                <UI.QuantitySelect
                  quantity={promotionQuantity}
                  onChange={handleChange}
                  maxQuantity={promotionQuantity + promotionAvailability}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  title={`${ticket.title} / ${promotion.title || t('selector.standard')}`}
                />
              )}
            </UI.GridContainer>
          )}

          {!promotion.is_sold_out && Object.values(groupedTimeSlots).map((timeSlots) => (
            <UI.GridContainer sc={{ gutter: 0.75 }} key={timeSlots[0].id}>
              {ticket.promotions_for_sale.length === 1 && (
                <UI.HR sc={{ mt: 0, mb: [2.25, 3] }} />
              )}

              <TimeSlotList
                ticket={ticket}
                promotion={promotion}
                timeSlots={timeSlots}
                isMultiDate={isMultiDate}
                {...getTimeSlotQuantitiesAndAvailability(timeSlots)}
                onChange={onChange}
                handleFocus={handleFocus}
                handleBlur={handleBlur}
              />
            </UI.GridContainer>
          ))}
        </UI.GridContainer>
      </UI.Div>
    );
  },
  (prevProps, newProps) => isEqual(prevProps, newProps),
);

PromotionSelect.displayName = 'PromotionSelect';

interface TimeSlotListProps {
  ticket: Ticket;
  promotion: Promotion;
  timeSlots: TimeSlot[];
  isMultiDate: boolean;
  timeSlotQuantities: { [timeSlotId: string]: number; };
  timeSlotAvailabilities: { [timeSlotId: string]: number; };
  onChange: (quantity: number, ticket: Ticket, promotion: Promotion, timeSlot?: TimeSlot) => void;
  handleFocus: () => void;
  handleBlur: () => void;
}

/**
 * Memoized using Lodash' isEqual() method because the quantities and availabilities are not stable.
 * This prevents unnecessary rerenders which is especially useful when there are many time slots.
 */
const TimeSlotList = memo(
  ({
    ticket, promotion, timeSlots, isMultiDate, timeSlotQuantities, timeSlotAvailabilities, onChange, handleFocus,
    handleBlur,
  }: TimeSlotListProps) => {
    const { t } = useTranslation();
    const { formatDate, parseDate } = useLocale();
    const formatTimeSlot = useTimeSlotFormatter();

    const timeSlotGroupIsSelected = (timeSlots: TimeSlot[]) => (
      timeSlots.filter((timeSlot) => timeSlotQuantities[timeSlot.id] > 0).length > 0
    );

    const isGrouped = isMultiDate && timeSlots.length > 1;
    const [showingTimeSlots, setShowingTimeSlots] = useState(!isGrouped || timeSlotGroupIsSelected(timeSlots));

    const toggleTimeSlots = () => setShowingTimeSlots((showing) => !showing);

    const formatDayOfWeek = (timeSlot: TimeSlot) => upperFirst(formatDate(
      parseDate(timeSlot.start_date, { format: 'internal_date' }),
      { format: 'display_day_of_week' },
    ));

    return (
      <>
        {isGrouped && (
          <UI.GridContainer sc={{ columns: '1fr fit-content(120px)', gutter: 0.5 }}>
            <UI.GridContainer sc={{ columns: '12px 1fr', gutter: 0.5 }}>
              <UI.Div>
                <UI.Icon sc={{ muted: true }}>
                  <Calendar />
                </UI.Icon>
              </UI.Div>
              <UI.Div>
                <UI.InputLabel>
                  {formatDayOfWeek(timeSlots[0])}
                </UI.InputLabel>
                {formatDate(
                  parseDate(timeSlots[0].start_date, { format: 'internal_date' }),
                  { format: 'display_date' },
                )}
              </UI.Div>
            </UI.GridContainer>
            <UI.Div>
              <UI.Button
                onClick={() => toggleTimeSlots()}
                sc={{ brand: !showingTimeSlots ? 'secondary' : undefined }}
                disabled={timeSlotGroupIsSelected(timeSlots)}
                aria-label={t('selector.choose_item', {
                  title: `${ticket.title} / ${promotion.title || t('selector.standard')} / ${formatTimeSlot({ ...timeSlots[0], start_time: null })}`,
                })}
              >
                {t('selector.choose')}
                {' '}
                <UI.Icon>
                  {!showingTimeSlots && <ChevronDown />}
                  {showingTimeSlots && <ChevronUp />}
                </UI.Icon>
              </UI.Button>
            </UI.Div>
          </UI.GridContainer>
        )}
        <UI.AnimateHeight isVisible={showingTimeSlots}>
          <UI.GridContainer sc={{ gutter: 0.75 }}>
            {timeSlots.map((timeSlot) => (
              <TimeSlotSelect
                ticket={ticket}
                promotion={promotion}
                timeSlot={timeSlot}
                quantity={timeSlotQuantities[timeSlot.id]}
                showDate={isMultiDate && !isGrouped}
                onChange={onChange}
                maxQuantity={timeSlotQuantities[timeSlot.id] + timeSlotAvailabilities[timeSlot.id]}
                availability={timeSlotAvailabilities[timeSlot.id]}
                onFocus={handleFocus}
                onBlur={handleBlur}
                key={timeSlot.id}
              />
            ))}
          </UI.GridContainer>
        </UI.AnimateHeight>
      </>
    );
  },
  (prevProps, newProps) => isEqual(prevProps, newProps),
);

TimeSlotList.displayName = 'TimeSlotList';

interface TimeSlotSelectProps {
  ticket: Ticket;
  promotion: Promotion;
  timeSlot: TimeSlot;
  quantity: number;
  showDate?: boolean;
  maxQuantity?: number;
  availability: number;
  onChange: (quantity: number, ticket: Ticket, promotion: Promotion, timeSlot?: TimeSlot) => void;
  onFocus: () => void;
  onBlur: () => void;
}

/**
 * Memoized because there are potentially many time slots, especially if there are also multiple promotions.
 */
const TimeSlotSelect = memo(({
  ticket, promotion, timeSlot, quantity, showDate = false, maxQuantity, availability, onChange, onFocus, onBlur,
}: TimeSlotSelectProps) => {
  const { t } = useTranslation();
  const formatTimeSlot = useTimeSlotFormatter();

  const handleChange = useCallback(
    (quantity: number) => {
      onChange(quantity, ticket, promotion, timeSlot);
    },
    [onChange, ticket, promotion, timeSlot],
  );

  return (
    <UI.GridContainer sc={{ columns: '1fr 143px', alignVertical: 'center', gutter: 0.5 }}>
      <UI.GridContainer sc={{ columns: '12px 1fr', gutter: 0.5 }}>
        <UI.Div>
          <UI.Icon sc={{ muted: true }}><Clock /></UI.Icon>
        </UI.Div>
        <UI.Div sc={{ muted: availability === 0 }}>
          <UI.InputLabel>
            {formatTimeSlot({ ...timeSlot, multi_date: showDate })}
          </UI.InputLabel>
          {timeSlot.title}
        </UI.Div>
      </UI.GridContainer>

      <UI.FlexContainer sc={{ justifyContent: 'flex-end' }}>
        {!timeSlot.is_sold_out && (
          <UI.QuantitySelect
            quantity={quantity}
            onChange={handleChange}
            maxQuantity={maxQuantity}
            onFocus={onFocus}
            onBlur={onBlur}
            title={`${ticket.title} / ${promotion.title || t('selector.standard')} / ${formatTimeSlot(timeSlot)}`}
          />
        )}
        {timeSlot.is_sold_out && (
          <SoldOutLabel title={`${ticket.title} / ${promotion.title || t('selector.standard')} / ${formatTimeSlot(timeSlot)}`} />
        )}
      </UI.FlexContainer>
    </UI.GridContainer>
  );
});

TimeSlotSelect.displayName = 'TimeSlotSelect';

/**
 * Inserts a given ticket into the list of registrations and sorts all registrations.
 */
const insertRegistration = (
  registrations: CreateRegistrationInput[],
  ticket: Ticket,
  promotion: Promotion,
  timeSlot: TimeSlot | undefined,
  quantity: number,
  products: Product[],
  assigned: boolean,
) => {
  const sameRegistrations: CreateRegistrationInput[] = [];
  const otherRegistrations: CreateRegistrationInput[] = [];

  registrations.forEach((registration) => {
    if (registration.purchase.promotion.id === promotion.id
      && (!timeSlot || registration.time_slot?.id === timeSlot.id)
    ) {
      if (sameRegistrations.length < quantity) {
        sameRegistrations.push(registration);
      }
    } else {
      otherRegistrations.push(registration);
    }
  });

  const missingQuantity = quantity - sameRegistrations.length;

  // Add automatic ticket fees and required products as upgrades
  const upgrades = getAdditionalProducts(promotion, products);

  const result = [
    ...otherRegistrations,
    ...sameRegistrations,
    ...times(missingQuantity, () => ({
      ticket: { id: ticket.id },
      ...(timeSlot ? {
        time_slot: { id: timeSlot.id },
      } : {}),
      purchase: {
        promotion: { id: promotion.id },
      },
      participant: assigned ? {} : null,
      details: assigned ? {} : null,
      fields: assigned ? [] : null,
      upgrades,
    })),
  ];

  return result;
};

const TicketCategoryButton = styled(UI.Link)<SC<{ active: boolean; selected: boolean; }>>`
  ${({ sc: { active, selected }, theme }) => css`
    display: flex;
    align-items: center;
    border-radius: ${theme.borderRadiuses.xl}px;
    background: ${theme.colors.gray[75]};
    background: linear-gradient(0deg, ${theme.transparentize(theme.colors.gray[75], 0.5)} 0%, ${theme.colors.gray[75]} 100%);
    color: ${theme.colors.gray[500]};
    padding: ${theme.gutter / 2}px;
    transition: all 0.15s ease-in-out;

    > ${UI.Icon}:first-child {
      color: ${theme.colors.secondary[500]};
      margin-right: ${theme.gutter / 2}px;
    }

    ${UI.Badge} {
      background: ${theme.transparentize(theme.colors.gray[400], selected ? 0.2 : 0.8)};
      color: ${selected ? theme.colors.white : theme.colors.gray[800]};
      transition: all 0.15s ease-in-out;
    }

    &:hover {
      background: linear-gradient(0deg, ${theme.colors.gray[75]} 0%, ${theme.colors.gray[100]} 100%);
      color: ${theme.colors.gray[800]};
    }

    ${active && css`
      background: linear-gradient(0deg, ${theme.colors.gray[75]} 0%, ${theme.colors.gray[100]} 100%);
      color: ${theme.colors.gray[800]};
    `}
  `}
`;

export default TicketSelector;
